/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useRef } from "react";
import PropTypes from "prop-types";
import { HEADER_VARIANTS, TABLE_VARIANTS, TABLE_ROWS, DEFAULT_VISIBLE_ROWS } from "./receiptTemplateDefaults";
import ReceiptRichTextEditor from "./ReceiptRichTextEditor";
import CredentialedImage from "./CredentialedImage";

const DEFAULT_FOOTER_DONATION = "1. This is an electronic receipt. 此為電子收據。\n2. We are a tax exempt charitable organization under Section 88 of the HK Inland Revenue Ordinance. For donation HK$100 or more, you are entitled to apply for tax deduction. 本機構是根據香港《稅務條例》第88條獲豁免繳稅的慈善機構，凡捐款 HK$100 或以上，捐款人可憑正式收據在報稅時申請扣除。";
const DEFAULT_FOOTER_PAYMENT = "1. This is an electronic receipt. 此為電子收據。\n2. Please retain this receipt for reference only. 請保留此收據作為付款紀錄。";

function ElementPropertyPanel({
  element,
  onUpdateElement,
  onDeleteElement,
  onUploadReceiptLogo,
  organizationObj,
  receiptType,
  previewDonorType,
  textObj = {}
}) {
  const t = (key, fallback) => textObj[key] || fallback;

  if (!element) {
    return (
      <div className="ElementPropertyPanel-panel">
        <div className="ElementPropertyPanel-empty">
          {t("clickToEdit", "Click an element on the canvas to edit its properties.")}
        </div>
      </div>
    );
  }

  const isFixed = element.type === "fixed";
  const isText = element.type === "custom-text";
  const isImage = element.type === "custom-image";

  const handleStyleChange = (prop, value) => {
    onUpdateElement(element.id, {
      style: {
        ...(element.style || {}),
        [prop]: value
      }
    });
  };

  const renderFontSizeRow = (currentValue, defaultValue, onChange) => (
    <div className="ElementPropertyPanel-row">
      <label className="ElementPropertyPanel-label">{t("fontSize", "Font Size")}</label>
      <div className="ElementPropertyPanel-fontSizeRow">
        <input
          type="number"
          className="ElementPropertyPanel-input ElementPropertyPanel-fontSizeInput"
          value={currentValue}
          min={4}
          max={72}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || defaultValue)}
        />
        {currentValue !== defaultValue && (
          <button
            type="button"
            className="ElementPropertyPanel-defaultBtn"
            onClick={() => onChange(defaultValue)}
            title={`${t("resetDefault", "Reset to default")} (${defaultValue})`}
          >
            {t("default", "Default")}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="ElementPropertyPanel-panel">
      <div className="ElementPropertyPanel-header">
        {isFixed && `${t("fixed", "Fixed: ")}${element.tag}`}
        {isText && t("customText", "Custom Text")}
        {isImage && t("customImage", "Custom Image")}
      </div>

      {/* Fixed element: visibility toggle */}
      {isFixed && (
        <div className="ElementPropertyPanel-row">
          <label className="ElementPropertyPanel-label">
            <input
              type="checkbox"
              checked={element.visible !== false}
              onChange={(e) =>
                onUpdateElement(element.id, { visible: e.target.checked })
              }
            />
            <span style={{ marginLeft: 8 }}>{t("visible", "Visible")}</span>
          </label>
        </div>
      )}

      {/* Header variant selector */}
      {isFixed && element.tag === "header" && (
        <>
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("headerStyle", "Header Style")}</label>
            <div className="ElementPropertyPanel-variants">
              {HEADER_VARIANTS.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  className={
                    "ElementPropertyPanel-variantBtn" +
                    ((element.variant || "classic") === v.id
                      ? " ElementPropertyPanel-variantBtn--active"
                      : "")
                  }
                  onClick={() => onUpdateElement(element.id, { variant: v.id })}
                >
                  <div className="ElementPropertyPanel-variantLabel">{v.label}</div>
                  <div className="ElementPropertyPanel-variantDesc">{v.description}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("backgroundColor", "Background Color")}</label>
            <input
              type="color"
              className="ElementPropertyPanel-colorInput"
              value={"#" + (element.headerColor || (organizationObj && organizationObj.headerColor) || "333333")}
              onChange={(e) =>
                onUpdateElement(element.id, { headerColor: e.target.value.replace("#", "") })
              }
            />
          </div>
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("textColor", "Text Color")}</label>
            <input
              type="color"
              className="ElementPropertyPanel-colorInput"
              value={"#" + (element.textColor || (organizationObj && organizationObj.buttonColor) || "ffffff")}
              onChange={(e) =>
                onUpdateElement(element.id, { textColor: e.target.value.replace("#", "") })
              }
            />
          </div>
          {renderFontSizeRow(element.fontSize || 6, 6, (v) => onUpdateElement(element.id, { fontSize: v }))}
          <ReceiptLogoUpload
            currentLogo={element.receiptLogo || (organizationObj && organizationObj.logo) || ""}
            orgLogo={(organizationObj && organizationObj.logo) || ""}
            onUpload={onUploadReceiptLogo}
            onRemove={() => onUpdateElement(element.id, { receiptLogo: "" })}
            onUseOrgLogo={() => onUpdateElement(element.id, { receiptLogo: "" })}
            hasOverride={!!element.receiptLogo}
            textObj={textObj}
          />
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("headerFields", "Header Fields")}</label>
            <div className="ElementPropertyPanel-checkboxList">
              <div className="ElementPropertyPanel-fieldItem">
                <label className="ElementPropertyPanel-checkboxItem">
                  <input
                    type="checkbox"
                    checked={element.showOrgName !== false}
                    onChange={(e) => onUpdateElement(element.id, { showOrgName: e.target.checked })}
                  />
                  <span>{t("orgName", "Organization Name")}</span>
                </label>
                {element.showOrgName !== false && (
                  <>
                    <div className="ElementPropertyPanel-fieldLabels">
                      <input
                        type="text"
                        className="ElementPropertyPanel-fieldLabelInput"
                        placeholder={t("orgNameLabel", "Name of Organization 機構名稱")}
                        value={element.orgNameLabel || ""}
                        onChange={(e) => onUpdateElement(element.id, { orgNameLabel: e.target.value })}
                      />
                    </div>
                    <div className="ElementPropertyPanel-fieldLabels">
                      <input
                        type="text"
                        className="ElementPropertyPanel-fieldLabelInput"
                        placeholder={(organizationObj && organizationObj.organizationName) || t("orgName", "Organization Name")}
                        value={element.customOrgName || ""}
                        onChange={(e) => onUpdateElement(element.id, { customOrgName: e.target.value })}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="ElementPropertyPanel-fieldItem">
                <label className="ElementPropertyPanel-checkboxItem">
                  <input
                    type="checkbox"
                    checked={element.showAddress !== false}
                    onChange={(e) => onUpdateElement(element.id, { showAddress: e.target.checked })}
                  />
                  <span>{t("orgAddress", "Organization Address")}</span>
                </label>
                {element.showAddress !== false && (
                  <>
                    <div className="ElementPropertyPanel-fieldLabels">
                      <input
                        type="text"
                        className="ElementPropertyPanel-fieldLabelInput"
                        placeholder={t("orgAddress", "Organization Address")}
                        value={element.addressLabel || ""}
                        onChange={(e) => onUpdateElement(element.id, { addressLabel: e.target.value })}
                      />
                    </div>
                    <div className="ElementPropertyPanel-fieldLabels">
                      <input
                        type="text"
                        className="ElementPropertyPanel-fieldLabelInput"
                        placeholder={(organizationObj && organizationObj.organizationAddress) || t("address", "Address")}
                        value={element.customAddress || ""}
                        onChange={(e) => onUpdateElement(element.id, { customAddress: e.target.value })}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="ElementPropertyPanel-fieldItem">
                <label className="ElementPropertyPanel-checkboxItem">
                  <input
                    type="checkbox"
                    checked={element.showIRDNo !== false}
                    onChange={(e) => onUpdateElement(element.id, { showIRDNo: e.target.checked })}
                  />
                  <span>{t("irdFileNo", "IRD File No.")}</span>
                </label>
                {element.showIRDNo !== false && (
                  <>
                    <div className="ElementPropertyPanel-fieldLabels">
                      <input
                        type="text"
                        className="ElementPropertyPanel-fieldLabelInput"
                        placeholder={t("irdFileNo", "IRD File No.")}
                        value={element.irdNoLabel || ""}
                        onChange={(e) => onUpdateElement(element.id, { irdNoLabel: e.target.value })}
                      />
                    </div>
                    <div className="ElementPropertyPanel-fieldLabels">
                      <input
                        type="text"
                        className="ElementPropertyPanel-fieldLabelInput"
                        placeholder={(organizationObj && organizationObj.organizationIRDNo) || t("irdFileNo", "IRD File No.")}
                        value={element.customIRDNo || ""}
                        onChange={(e) => onUpdateElement(element.id, { customIRDNo: e.target.value })}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Table variant selector */}
      {isFixed && element.tag === "table" && (
        <>
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("tableStyle", "Table Style")}</label>
            <div className="ElementPropertyPanel-variants">
              {TABLE_VARIANTS.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  className={
                    "ElementPropertyPanel-variantBtn" +
                    ((element.variant || "standard") === v.id
                      ? " ElementPropertyPanel-variantBtn--active"
                      : "")
                  }
                  onClick={() => onUpdateElement(element.id, { variant: v.id })}
                >
                  <div className="ElementPropertyPanel-variantLabel">{v.label}</div>
                  <div className="ElementPropertyPanel-variantDesc">{v.description}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">
              {t("labelColumnWidth", "Label Column Width: ")}{element.labelWidth || 20}%
            </label>
            <input
              type="range"
              className="ElementPropertyPanel-range"
              min={20}
              max={60}
              value={element.labelWidth || 20}
              onChange={(e) =>
                onUpdateElement(element.id, { labelWidth: parseInt(e.target.value, 10) })
              }
            />
            <div className="ElementPropertyPanel-rangeLabels">
              <span>20%</span>
              <span>60%</span>
            </div>
          </div>
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">
              {t("rowHeight", "Row Height: ")}{element.rowHeight || 50}px
            </label>
            <input
              type="range"
              className="ElementPropertyPanel-range"
              min={30}
              max={80}
              value={element.rowHeight || 50}
              onChange={(e) =>
                onUpdateElement(element.id, { rowHeight: parseInt(e.target.value, 10) })
              }
            />
            <div className="ElementPropertyPanel-rangeLabels">
              <span>30px</span>
              <span>80px</span>
            </div>
          </div>
          {renderFontSizeRow(element.fontSize || 6, 6, (v) => onUpdateElement(element.id, { fontSize: v }))}
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("columns", "Columns")}</label>
            <div className="ElementPropertyPanel-columnBtns">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={
                    "ElementPropertyPanel-columnBtn" +
                    ((element.columns || 1) === n ? " ElementPropertyPanel-columnBtn--active" : "")
                  }
                  onClick={() => onUpdateElement(element.id, { columns: n })}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("visibleFields", "Visible Fields")}</label>
            <div className="ElementPropertyPanel-checkboxList">
              {TABLE_ROWS.map((row) => {
                if (row.key === "donorContactPerson" && previewDonorType === "individual") return null;
                const rows = element.visibleRows || DEFAULT_VISIBLE_ROWS;
                const checked = rows[row.key] !== false;
                const cl = (element.customLabels && element.customLabels[row.key]) || {};
                return (
                  <div key={row.key} className="ElementPropertyPanel-fieldItem">
                    <label className="ElementPropertyPanel-checkboxItem">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          onUpdateElement(element.id, {
                            visibleRows: {
                              ...(element.visibleRows || DEFAULT_VISIBLE_ROWS),
                              [row.key]: e.target.checked
                            }
                          })
                        }
                      />
                      <span>{row.label}</span>
                    </label>
                    {checked && (
                      <div className="ElementPropertyPanel-fieldLabels">
                        <input
                          type="text"
                          className="ElementPropertyPanel-fieldLabelInput"
                          placeholder={row.defaultEn || row.label}
                          value={cl.en || ""}
                          onChange={(e) =>
                            onUpdateElement(element.id, {
                              customLabels: {
                                ...(element.customLabels || {}),
                                [row.key]: { ...cl, en: e.target.value }
                              }
                            })
                          }
                        />
                        <input
                          type="text"
                          className="ElementPropertyPanel-fieldLabelInput"
                          placeholder={row.defaultZh || "中文"}
                          value={cl.zh || cl.cn || ""}
                          onChange={(e) =>
                            onUpdateElement(element.id, {
                              customLabels: {
                                ...(element.customLabels || {}),
                                [row.key]: { ...cl, zh: e.target.value }
                              }
                            })
                          }
                        />
                        {row.hasOrganizationVariant && (
                          <>
                            <input
                              type="text"
                              className="ElementPropertyPanel-fieldLabelInput"
                              placeholder={row.defaultEnOrganization || "Organization"}
                              value={cl.enOrganization || ""}
                              onChange={(e) =>
                                onUpdateElement(element.id, {
                                  customLabels: {
                                    ...(element.customLabels || {}),
                                    [row.key]: { ...cl, enOrganization: e.target.value }
                                  }
                                })
                              }
                            />
                            <input
                              type="text"
                              className="ElementPropertyPanel-fieldLabelInput"
                              placeholder={row.defaultZhOrganization || "捐款機構"}
                              value={cl.zhOrganization || cl.cnOrganization || ""}
                              onChange={(e) =>
                                onUpdateElement(element.id, {
                                  customLabels: {
                                    ...(element.customLabels || {}),
                                    [row.key]: { ...cl, zhOrganization: e.target.value }
                                  }
                                })
                              }
                            />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Individual custom field checkboxes */}
          {organizationObj && organizationObj.formSetting && organizationObj.formSetting.enabled &&
            organizationObj.formSetting.rows && organizationObj.formSetting.rows.filter((f) => !f.archived).length > 0 && (
            <div className="ElementPropertyPanel-row">
              <label className="ElementPropertyPanel-label">{t("customFields", "Custom Fields")}</label>
              <div className="ElementPropertyPanel-checkboxList">
                {organizationObj.formSetting.rows
                  .filter((f) => !f.archived)
                  .map((field) => {
                    const vcf = element.visibleCustomFields || {};
                    const checked = vcf[field.id] !== false;
                    const label = (field.i18n && field.i18n.label && field.i18n.label.en) || field.id;
                    return (
                      <label key={field.id} className="ElementPropertyPanel-checkboxItem">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            onUpdateElement(element.id, {
                              visibleCustomFields: {
                                ...vcf,
                                [field.id]: e.target.checked
                              }
                            })
                          }
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Header: additional text */}
      {isFixed && element.tag === "header" && (
        <div className="ElementPropertyPanel-row">
          <label className="ElementPropertyPanel-label">{t("additionalHeaderText", "Additional Header Text")}</label>
          <ReceiptRichTextEditor
            key={element.id + "-additional"}
            content={element.additionalContent}
            onContentChange={(raw) =>
              onUpdateElement(element.id, { additionalContent: raw })
            }
          />
        </div>
      )}

      {/* Heading: title + font size + additional text */}
      {isFixed && element.tag === "heading" && (
        <>
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("headingText", "Heading Text")}</label>
            <input
              type="text"
              className="ElementPropertyPanel-input"
              placeholder="Official Receipt 正式收據"
              value={element.customHeading || ""}
              onChange={(e) =>
                onUpdateElement(element.id, { customHeading: e.target.value })
              }
            />
          </div>
          {renderFontSizeRow(element.fontSize || 10, 10, (v) => onUpdateElement(element.id, { fontSize: v }))}
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("additionalText", "Additional Text")}</label>
            <ReceiptRichTextEditor
              key={element.id + "-additional"}
              content={element.additionalContent}
              onContentChange={(raw) =>
                onUpdateElement(element.id, { additionalContent: raw })
              }
            />
          </div>
        </>
      )}

      {/* Re-issuance note: text template + font size. #N is replaced
          with the re-issuance count at render time. Element only renders
          on actual receipts when the receipt has been re-issued (count > 0);
          editor preview always shows a sample. */}
      {isFixed && element.tag === "reissued" && (
        <>
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("reissuedText", "Re-issuance Text")}</label>
            <input
              type="text"
              className="ElementPropertyPanel-input"
              placeholder="Re-issuance #N 補發"
              value={element.customText || ""}
              onChange={(e) =>
                onUpdateElement(element.id, { customText: e.target.value })
              }
            />
            <div className="ElementPropertyPanel-hint">
              {t("reissuedHint", "Use #N to insert the re-issuance number.")}
            </div>
          </div>
          {renderFontSizeRow(element.fontSize || 6, 6, (v) => onUpdateElement(element.id, { fontSize: v }))}
        </>
      )}

      {/* Footer: font size + fully editable */}
      {isFixed && element.tag === "footer" && (
        renderFontSizeRow(element.fontSize || 6, 6, (v) => onUpdateElement(element.id, { fontSize: v }))
      )}
      {isFixed && element.tag === "footer" && (
        <div className="ElementPropertyPanel-row">
          <label className="ElementPropertyPanel-label">{t("footerText", "Footer Text")}</label>
          <ReceiptRichTextEditor
            key={element.id + "-footer-" + receiptType}
            content={element.footerContent || (receiptType === "payment" ? DEFAULT_FOOTER_PAYMENT : DEFAULT_FOOTER_DONATION)}
            onContentChange={(raw) =>
              onUpdateElement(element.id, { footerContent: raw })
            }
          />
        </div>
      )}

      {/* Text element: content and style */}
      {isText && (
        <>
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("content", "Content")}</label>
            <ReceiptRichTextEditor
              key={element.id}
              content={element.content}
              onContentChange={(raw) =>
                onUpdateElement(element.id, { content: raw })
              }
            />
          </div>
          {renderFontSizeRow(
            (element.style && element.style.fontSize) || 12,
            12,
            (v) => handleStyleChange("fontSize", v)
          )}
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("textColor", "Text Color")}</label>
            <input
              type="color"
              className="ElementPropertyPanel-colorInput"
              value={(element.style && element.style.color) || "#000000"}
              onChange={(e) => handleStyleChange("color", e.target.value)}
            />
          </div>
          <div className="ElementPropertyPanel-row">
            <label className="ElementPropertyPanel-label">{t("textAlign", "Text Align")}</label>
            <select
              className="ElementPropertyPanel-select"
              value={(element.style && element.style.textAlign) || "left"}
              onChange={(e) => handleStyleChange("textAlign", e.target.value)}
            >
              <option value="left">{t("alignLeft", "Left")}</option>
              <option value="center">{t("alignCenter", "Center")}</option>
              <option value="right">{t("alignRight", "Right")}</option>
            </select>
          </div>
        </>
      )}

      {/* Delete button for custom elements */}
      {!isFixed && (
        <div className="ElementPropertyPanel-row">
          <button
            type="button"
            className="ElementPropertyPanel-deleteBtn"
            onClick={() => onDeleteElement(element.id)}
          >
            {t("deleteElement", "Delete Element")}
          </button>
        </div>
      )}
    </div>
  );
}

function ReceiptLogoUpload({ currentLogo, orgLogo, onUpload, onRemove, onUseOrgLogo, hasOverride, textObj = {} }) {
  const fileRef = useRef(null);
  const t = (key, fallback) => textObj[key] || fallback;

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file && typeof onUpload === "function") {
      onUpload(file);
      e.target.value = "";
    }
  };

  return (
    <div className="ElementPropertyPanel-row">
      <label className="ElementPropertyPanel-label">{t("receiptLogo", "Receipt Logo")}</label>
      {currentLogo && (
        <div className="ElementPropertyPanel-logoPreview">
          <CredentialedImage src={currentLogo} alt={t("logo", "Logo")} style={{ maxWidth: "100%", maxHeight: 60, objectFit: "contain" }} />
        </div>
      )}
      <div className="ElementPropertyPanel-logoActions">
        <button
          type="button"
          className="ReceiptEditorToolbar-btn"
          onClick={() => fileRef.current && fileRef.current.click()}
        >
          {t("uploadLogo", "Upload Logo")}
        </button>
        {hasOverride && orgLogo && (
          <button
            type="button"
            className="ReceiptEditorToolbar-btn"
            onClick={onUseOrgLogo}
          >
            {t("useOrgLogo", "Use Org Logo")}
          </button>
        )}
        {hasOverride && (
          <button
            type="button"
            className="ReceiptEditorToolbar-btn ReceiptEditorToolbar-btn--reset"
            onClick={onRemove}
          >
            {t("remove", "Remove")}
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg"
        style={{ display: "none" }}
        onChange={handleChange}
      />
    </div>
  );
}

ReceiptLogoUpload.propTypes = {
  currentLogo: PropTypes.string,
  orgLogo: PropTypes.string,
  onUpload: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onUseOrgLogo: PropTypes.func.isRequired,
  hasOverride: PropTypes.bool,
  textObj: PropTypes.object
};

ElementPropertyPanel.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    tag: PropTypes.string,
    visible: PropTypes.bool,
    variant: PropTypes.string,
    headerColor: PropTypes.string,
    textColor: PropTypes.string,
    receiptLogo: PropTypes.string,
    showOrgName: PropTypes.bool,
    showAddress: PropTypes.bool,
    showIRDNo: PropTypes.bool,
    customOrgName: PropTypes.string,
    customAddress: PropTypes.string,
    customIRDNo: PropTypes.string,
    orgNameLabel: PropTypes.string,
    addressLabel: PropTypes.string,
    irdNoLabel: PropTypes.string,
    labelWidth: PropTypes.number,
    rowHeight: PropTypes.number,
    columns: PropTypes.number,
    visibleRows: PropTypes.object,
    visibleCustomFields: PropTypes.object,
    customLabels: PropTypes.object,
    fontSize: PropTypes.number,
    customHeading: PropTypes.string,
    customText: PropTypes.string,
    footerContent: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    additionalContent: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    src: PropTypes.string,
    style: PropTypes.shape({
      fontSize: PropTypes.number,
      fontWeight: PropTypes.string,
      color: PropTypes.string,
      textAlign: PropTypes.string
    })
  }),
  onUpdateElement: PropTypes.func.isRequired,
  onDeleteElement: PropTypes.func.isRequired,
  onUploadReceiptLogo: PropTypes.func,
  organizationObj: PropTypes.object,
  receiptType: PropTypes.string,
  previewDonorType: PropTypes.string,
  textObj: PropTypes.object
};

export default ElementPropertyPanel;
